// Shared scope controls for the District Insights dashboard: the pickers that
// decide WHICH data is on screen (organization, school year, school, period).
//
// The page frame itself lives in dashboard/chrome.js (DashShell); this file holds
// only the controls, so there is one frame and one place to change it.
import React, { useState, useEffect } from "react";
import { INK, MUTED, GRID, DIMENSIONS, STATUS } from "./tokens";
import { SegControl, Select } from "./ui";

// School-year picker. Two bounds decide what it holds (see syYearOptions in
// impact/schoolyear.js). How far back it *lists* is the district's own lifetime, so
// it never offers a year the org did not exist for. Which of those years are
// *selectable* follows how far back the enrollment ledger reaches: years the ledger
// covers outright read plain, a year straddling the ledger's start is marked
// "(reconstructed)", and years ending before it are listed but disabled — for those
// there is no enrollment history to reconstruct a roster from at all. Listed rather
// than omitted on purpose: a greyed row says "this district is older than its
// records", where a short list would read as "this district is new".
//
// `inline` because this lives in the page header strip beside the title, not in
// an in-page control cluster.
const YearSelect = ({ year, yearOptions, onYearChange }) => (
  <Select
    inline
    label="School year"
    value={year}
    onChange={(v) => onYearChange(parseInt(v, 10))}
    title="Years are listed back to this district's first year on ABii, and are selectable as far back as enrollment history was recorded. A year marked “reconstructed” uses enrollment as it stood at year end, so learners who left mid-year may be missing; earlier years are shown but unavailable."
    options={yearOptions.map((o) => [o.year, o.label, { disabled: o.disabled }])}
  />
);

// Organization scoper (superadmin only). A searchable typeahead over all orgs —
// the district picker that stands in for an org admin's fixed user.organization.
// Backed by the paginated /accounts/all-orgs/ search (?q=), the same endpoint the
// superadmin Orgs list uses. Deliberately simple: type to search, click to
// select, click outside to dismiss; no keyboard navigation.
//
// `align` is which edge the 320px panel hangs from, and it has to be told rather
// than guessed: in the page header this control sits at the right margin, where a
// left-hung panel would run off the window, and in an in-page control cluster it
// sits at the LEFT margin, where a right-hung panel opens back over the sidebar.
// Defaulting to "right" keeps the header callers as they were.
// The all-organizations pick: every district the account can see, in one view.
// A sentinel org rather than a separate control, because everything downstream
// already keys on `org.id` and threading a second "is this all orgs" flag
// through the scope hook, three load effects and the page props is how the two
// drift apart. `ALL_ORGS_ID` is the literal the backend reads as its `all`
// scope, so the value in state IS the value on the wire.
const ALL_ORGS_ID = "all";
const ALL_ORGS = { id: ALL_ORGS_ID, name: "All organizations" };
const isAllOrgs = (org) => !!org && org.id === ALL_ORGS_ID;

const OrgScopeSelect = ({ api, value, onChange, align = "right" }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  // Debounced search, only while the dropdown is open. A blank query fetches the
  // first alphabetical page so the list isn't empty before the user types.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setBusy(true);
    const handle = setTimeout(() => {
      api
        .fetchAllVisibleOrganizations([
          ["q", query],
          ["sort", "name"],
          ["page_size", 25],
        ])
        .then((res) => {
          if (!cancelled) setResults((res && res.results) || []);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setBusy(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, open]);

  const label =
    value && value.name
      ? value.name
      : value && value.id
      ? `Organization #${value.id}`
      : "Select organization…";

  return (
    <div style={{ position: "relative" }}>
      <label
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          fontSize: 13,
        }}
      >
        <span style={{ color: MUTED, fontWeight: 600 }}>Organization</span>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          title={label}
          style={{
            border: `1px solid ${GRID}`,
            borderRadius: 9,
            padding: "6px 10px",
            fontSize: 13,
            fontWeight: 600,
            color: value && value.id ? INK : MUTED,
            background: "#fff",
            cursor: "pointer",
            maxWidth: 260,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {label} ▾
        </button>
      </label>
      {open && (
        <>
          {/* click-away backdrop */}
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 10 }}
          />
          <div
            style={{
              position: "absolute",
              zIndex: 20,
              top: "calc(100% + 4px)",
              [align]: 0,
              width: 320,
              maxWidth: "80vw",
              background: "#fff",
              border: `1px solid ${GRID}`,
              borderRadius: 8,
              boxShadow: "0 6px 24px rgba(43,42,40,0.18)",
              padding: 8,
            }}
          >
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search organizations…"
              style={{
                width: "100%",
                boxSizing: "border-box",
                border: `1px solid ${GRID}`,
                borderRadius: 6,
                padding: "6px 10px",
                fontSize: 13,
                marginBottom: 6,
              }}
            />
            <div style={{ maxHeight: 280, overflowY: "auto" }}>
              {busy && (
                <div style={{ color: MUTED, fontSize: 12, padding: "6px 8px" }}>
                  Searching…
                </div>
              )}
              {!busy && results.length === 0 && (
                <div style={{ color: MUTED, fontSize: 12, padding: "6px 8px" }}>
                  No organizations found.
                </div>
              )}
              {/* Pinned above the search results and NOT filtered by the query:
                  it is a scope, not a district, so a typed search should narrow
                  the districts under it without making the way back to the
                  whole customer base disappear. */}
              <button
                type="button"
                onClick={() => {
                  onChange(ALL_ORGS);
                  setOpen(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  border: "none",
                  borderBottom: `1px solid ${GRID}`,
                  background: isAllOrgs(value) ? "#f0efe9" : "transparent",
                  color: INK,
                  borderRadius: 6,
                  padding: "7px 8px",
                  marginBottom: 4,
                  fontSize: 13,
                  fontWeight: isAllOrgs(value) ? 600 : 400,
                  cursor: "pointer",
                }}
              >
                {ALL_ORGS.name}
                <span style={{ color: MUTED, fontWeight: 400 }}>
                  {" "}
                  · every district
                </span>
              </button>
              {results.map((o) => {
                const active = value && value.id === o.id;
                return (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => {
                      onChange({ id: o.id, name: o.name });
                      setOpen(false);
                    }}
                    style={{
                      display: "block",
                      width: "100%",
                      textAlign: "left",
                      border: "none",
                      background: active ? "#f0efe9" : "transparent",
                      color: INK,
                      borderRadius: 6,
                      padding: "7px 8px",
                      fontSize: 13,
                      fontWeight: active ? 600 : 400,
                      cursor: "pointer",
                    }}
                  >
                    {o.name}
                    {o.state ? (
                      <span style={{ color: MUTED, fontWeight: 400 }}>
                        {" "}
                        · {o.state}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Dashboard-level "break down by" selector. The segmentation dimension is
// orthogonal to the metric tabs (impact by school is the same question as
// impact by teacher, just a different GROUP BY), so it's a single global
// control rather than per-tab options.
//
// `inline` for the page ControlBar, where it sits on one line beside the lens
// tabs; stacked micro-caps otherwise, for a vertical control cluster.
// `available` is the server's `dimensions_available` for the window on screen: a
// breakdown whose ledger edges don't reach it is shown disabled with the reason
// rather than hidden, because a control that silently loses an option reads as a
// bug, and "we can't reconstruct who taught then" is information worth stating.
// Null means no response has landed yet -- everything stays enabled, and the server
// refuses anything it can't answer.
const BreakdownControl = ({
  dimension,
  setDimension,
  inline,
  available,
  unavailableReason,
}) => (
  <SegControl
    label="Break down by"
    inline={inline}
    value={dimension}
    onChange={setDimension}
    options={DIMENSIONS.map(([key, label]) =>
      !available || available.includes(key)
        ? [key, label]
        : [key, label, { disabled: true, title: unavailableReason }]
    )}
  />
);

// School scoper for the teacher breakdown. Teacher segments are only meaningful
// (and un-crowded) within one building, so the teacher dimension drills into a
// chosen school rather than fanning out district-wide.
const SchoolSelect = ({ schools, value, onChange, inline }) => (
  <Select
    label="School"
    inline={inline}
    value={value}
    onChange={onChange}
    // a <select> takes its intrinsic width from its longest option, and school
    // names run long — cap it (same as the curriculum tab's school picker)
    maxWidth={240}
    options={
      schools.length === 0
        ? [["", "(no schools)"]]
        : schools.map((s) => [s.id, s.name])
    }
  />
);


// ---- focus-cycle period picker ---------------------------------------------
// Narrows the window from a whole school year to a *detected* focus cycle — the
// fall/spring stretch a district or school actually ran (vanroboapp.focus_cycles).
//
// Three things shape this control:
//
// 1. It loads lazily, on first open. Detection is 8–20s on a large district and
//    most visits never touch this control, so paying for it on page load would
//    tax everyone for a feature few use.
// 2. It follows scope. Cycles are detected per institution, and a district whose
//    schools push at different times has NO district cycle even when most of its
//    schools do (prod: Blue Springs scores 1.47x against a 1.5x gate while 14 of
//    its 16 schools each have a real push). So when the district comes back
//    empty we fan out across its schools rather than reporting "no cycles" —
//    that answer is true and useless.
// 3. Its options carry their own dates and intensity. A period is a window, not
//    a term of art, and in-cycle figures are selected-for by construction — the
//    label is the first place that provenance shows up.
const encodeCycle = (c) =>
  !c ? "" : c.school ? `s:${c.school}:${c.key}` : c.key;

// Detection results, cached at module scope so opening the control on one tab
// pays for the other too. Both the Learning and Engagement pages carry a Period
// picker, and without this, moving between them re-runs the entire fan-out —
// 21 requests and the better part of a minute on a 20-school district — for an
// answer that cannot have changed. Keyed by scope + window, which is exactly
// what detection depends on. Deliberately not bounded: one entry per (org,
// year) a single session actually looks at.
const CYCLE_CACHE = new Map();

// Detected cycles for ANNOTATION rather than for scoping — the district-level
// cycles, so both Adoption lenses can shade the stretch a push happened in while
// every number on screen still describes the whole window.
//
// Deliberately only the org-level call, never the per-school fan-out PeriodSelect
// falls back to. That fan-out is 21 requests and most of a minute on a 20-school
// district; paying it automatically, for a tint, on every page load is exactly the
// tax the picker's lazy loading exists to avoid. So a district whose schools push
// at different times gets no band, which is the honest answer at district scope —
// per-school bands are the adoption ribbon's job, and it can pay for its own data.
//
// Shares PeriodSelect's cache, and claims the shared key only on a non-empty
// result: an empty entry there would tell the picker "already checked, nothing
// found" and suppress the very fan-out that finds the schools' own cycles.
const useFocusCycles = ({ api, org, win, enabled = true }) => {
  const [cycles, setCycles] = useState(null);
  const orgId = org && org.id;
  const start = win && win.start;
  const end = win && win.end;
  useEffect(() => {
    if (!enabled || !orgId) {
      setCycles(null);
      return undefined;
    }
    const ck = `${orgId}|${start}|${end}`;
    const hit = CYCLE_CACHE.get(ck) || CYCLE_CACHE.get(`${ck}|org`);
    if (hit) {
      setCycles(hit.cycles || []);
      return undefined;
    }
    let cancelled = false;
    api
      .fetchFocusCycles({ scope: "organization", id: orgId, start, end })
      .then((r) => {
        const own = (r && r.cycles) || [];
        CYCLE_CACHE.set(own.length ? ck : `${ck}|org`, {
          cycles: own,
          rejected: (r && r.rejected) || [],
        });
        if (!cancelled) setCycles(own);
      })
      // an annotation that fails is simply absent; nothing else on the page
      // depends on it
      .catch(() => {
        if (!cancelled) setCycles([]);
      });
    return () => {
      cancelled = true;
    };
    // `api` is recreated every render by withAPI — depending on it never settles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, orgId, start, end]);
  return cycles;
};

const PeriodSelect = ({
  api,
  org,
  orgunits = [],
  win,
  value,
  onChange,
  inline,
}) => {
  const [state, setState] = useState({ status: "idle" });
  const orgId = org && org.id;
  const winStart = win && win.start;
  const winEnd = win && win.end;

  // Any change of scope or window invalidates what we fetched. Drop back to
  // "idle" (not to a stale list) so the next open re-detects.
  useEffect(() => {
    setState({ status: "idle" });
  }, [orgId, winStart, winEnd]);

  const load = () => {
    if (state.status !== "idle" || !orgId) return;
    const { start, end } = win || {};
    const ck = `${orgId}|${start}|${end}`;
    const hit = CYCLE_CACHE.get(ck);
    if (hit) {
      setState({ status: "done", ...hit });
      return;
    }
    setState({ status: "busy" });
    const tag = (list, ou) =>
      (list || []).map((c) => ({
        ...c,
        school: ou ? ou.id : null,
        schoolName: ou ? ou.name : null,
      }));
    api
      .fetchFocusCycles({ scope: "organization", id: orgId, start, end })
      .then((district) => {
        const own = tag(district && district.cycles);
        if (own.length)
          return { cycles: own, rejected: (district && district.rejected) || [] };
        return Promise.all(
          orgunits.map((ou) =>
            api
              .fetchFocusCycles({ scope: "orgunit", id: ou.id, start, end })
              .then((r) => tag(r && r.cycles, ou))
              .catch(() => [])
          )
        ).then((parts) => ({
          cycles: [].concat(...parts),
          rejected: (district && district.rejected) || [],
          staggered: true,
        }));
      })
      .then((r) => {
        CYCLE_CACHE.set(ck, r);
        setState({ status: "done", ...r });
      })
      // a failure is NOT cached -- reopening the control should retry
      .catch(() => setState({ status: "done", cycles: [], failed: true }));
  };

  const cycles = state.cycles || [];
  const busy = state.status === "busy";
  const options = [["", "Whole year"]];
  if (busy) options.push(["__busy", "Finding focus cycles…", { disabled: true }]);
  cycles.forEach((c) => {
    const range = `${fmtShort(c.start)} – ${fmtShort(c.end)}`;
    options.push([
      encodeCycle(c),
      c.schoolName
        ? `${c.schoolName} · ${c.label} · ${range} · ${c.intensity}×`
        : `${c.label} · ${range} · ${c.intensity}×`,
    ]);
  });

  // Why the list is empty, in the user's terms. `rejected` reasons are
  // diagnostic strings meant for us, so they're summarized rather than shown.
  let note = null;
  if (state.status === "done" && !cycles.length) {
    if (state.failed) note = "Couldn’t check for focus cycles.";
    else if (state.staggered)
      note =
        "No district-wide push, and no school ran one either — usage is spread evenly.";
    else note = "No distinct push — usage is spread evenly across the year.";
  } else if (state.status === "done" && state.staggered)
    note = "No district-wide push; these are individual schools’ cycles.";

  // In a control bar the "why is this list empty" note can't be a block under the
  // select — it would push the bar's height back up, in exactly the case where
  // the control turned out to have nothing to offer. It becomes a hoverable mark
  // instead, on the same line.
  if (inline)
    return (
      <div
        onMouseDown={load}
        onFocus={load}
        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
      >
        <Select
          inline
          label="Period"
          value={encodeCycle(value)}
          onChange={(v) =>
            onChange(cycles.find((c) => encodeCycle(c) === v) || null)
          }
          maxWidth={300}
          title="Narrow the year to a focus cycle — a stretch this district or school actually ran ABii hard. Figures inside a cycle describe that push, not the year."
          options={options}
        />
        {note && (
          <span
            title={note}
            aria-label={note}
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: MUTED,
              border: `1px solid ${GRID}`,
              borderRadius: "50%",
              width: 15,
              height: 15,
              lineHeight: "14px",
              textAlign: "center",
              cursor: "help",
            }}
          >
            i
          </span>
        )}
      </div>
    );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <div onMouseDown={load} onFocus={load}>
        <Select
          label="Period"
          value={encodeCycle(value)}
          onChange={(v) =>
            onChange(cycles.find((c) => encodeCycle(c) === v) || null)
          }
          maxWidth={340}
          title="Narrow the year to a focus cycle — a stretch this district or school actually ran ABii hard. Figures inside a cycle describe that push, not the year."
          options={options}
        />
      </div>
      {note && (
        <div style={{ fontSize: 11.5, color: MUTED, maxWidth: 340 }}>{note}</div>
      )}
    </div>
  );
};

// Export the current scope + window as a file. Sits in the dashboard chrome
// beside the org and year pickers because that is exactly what it exports: the
// scope those two controls resolve, at the grain a district can re-aggregate
// (one row per learner). It is not a picture of the page below it -- every panel
// there is an aggregate that has already discarded the join keys, so a
// per-panel export would be unimportable by construction.
//
// The button owns its own busy state rather than taking one as a prop: this is
// the one control on the page whose work is not part of the dashboard's data
// spine, and hoisting it would put a district-sized query in front of a render.
// On a large district the request runs long enough that the label has to say so,
// or the admin clicks it again.
const ExportButton = ({ api, scope, id, start, end, standardset }) => {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const run = () => {
    if (busy || !id) return;
    setBusy(true);
    setFailed(false);
    api
      .exportInsights({ scope, id, start, end, standardset })
      .catch(() => setFailed(true))
      .finally(() => setBusy(false));
  };

  return (
    <button
      type="button"
      onClick={run}
      disabled={busy || !id}
      title={
        failed
          ? "The export failed. Try again, or narrow the school year."
          : "Download this district's learners for the selected school year as a CSV — one row per learner, with usage and standards mastery."
      }
      style={{
        border: `1px solid ${failed ? STATUS.critical.color : GRID}`,
        borderRadius: 9,
        padding: "6px 10px",
        fontSize: 13,
        fontWeight: 600,
        color: failed ? STATUS.critical.color : INK,
        background: "#fff",
        cursor: busy || !id ? "default" : "pointer",
        opacity: busy || !id ? 0.6 : 1,
        whiteSpace: "nowrap",
      }}
    >
      {busy ? "Preparing…" : failed ? "Export failed — retry" : "Export CSV"}
    </button>
  );
};

// "2025-09-12T00:00:00+00:00" -> "Sep 12". Formatted from the date part in UTC:
// these are day boundaries, and letting the browser localize them slides a
// cycle's label a day either way depending on who's looking.
const fmtShort = (iso) =>
  new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });

export {
  YearSelect,
  OrgScopeSelect,
  ALL_ORGS,
  ALL_ORGS_ID,
  isAllOrgs,
  ExportButton,
  BreakdownControl,
  SchoolSelect,
  PeriodSelect,
  useFocusCycles,
  encodeCycle,
};
